import Vue from 'vue';
import { RawLocation } from 'vue-router';
import { SECRET } from '@/store/types';
import { showToast } from '@/store/utils';
import { findCache } from '@/store/cache';
import { SecretPost, SecretRoom } from '@/api/types/secret';
import { standardizePostsRoute, stringifyPostsRoute } from './secret';
import { isPrivateRoom, canReadPrivateRoom } from './private-room';

export const privateContentVisibility = Vue.observable({ checking: false });

// Server checks run on navigation and foreground resume; no client billing timer.
export const installPrivateRoomContent = (router, store): void => {
  const protectedRoutes: Record<string, number> = {};
  let activeChecks = 0;
  let expiryTimer: ReturnType<typeof setTimeout> | null = null;
  const roomForRoute = async (route): Promise<number> => {
    if (route.name === 'secret_create' && route.query.privateRoom) return Number(route.query.privateRoom);
    if (route.name === 'secret_list') {
      const parsed = standardizePostsRoute(route);
      if (parsed.params.p0 !== 'room') return 0;
      const id = Number(parsed.params.p2 || parsed.params.p1);
      await store.dispatch(`secret/room/${SECRET.GET_ROOM}`, { id, action: 'refresh' });
      const room = findCache<SecretRoom>(store.state.secret.room.cacheRooms, id);
      return isPrivateRoom(room) ? id : 0;
    }
    if (route.name === 'secret_detail') {
      // POST_GET must await fresh data before cached post content can be displayed.
      await store.dispatch(`secret/post/${SECRET.POST_GET}`, { id: route.params.id });
      const post = findCache<SecretPost>(store.state.secret.post.cache, route.params.id);
      if (!post || !post.room) return 0;
      await store.dispatch(`secret/room/${SECRET.GET_ROOM}`, { id: post.room.id, action: 'refresh' });
      return isPrivateRoom(findCache<SecretRoom>(store.state.secret.room.cacheRooms, post.room.id)) ? post.room.id : 0;
    }
    return 0;
  };
  const check = async (route): Promise<RawLocation | null> => {
    const id = await roomForRoute(route);
    if (!id) return null;
    protectedRoutes[route.fullPath] = id;
    const rights = await store.dispatch('secret/privateRooms/detail', { id });
    if (expiryTimer) clearTimeout(expiryTimer);
    if (rights.expiresAt && rights.expiresAt * 1000 > Date.now()) {
      expiryTimer = setTimeout(() => {
        store.dispatch('secret/privateRooms/invalidate');
      }, Math.min(rights.expiresAt * 1000 - Date.now(), 2147483647));
    }
    return (route.name === 'secret_create' ? rights.canPost : canReadPrivateRoom(rights))
      ? null
      : { name: 'private_room', params: { id: String(id) } };
  };
  router.beforeEach(async (to, from, next) => {
    try { next(await check(to) || void 0); } catch (error) {
      // Never reveal a cached protected page when a permission check is unavailable.
      if (protectedRoutes[to.fullPath] || to.query.privateRoom) next({ name: 'private_rooms' });
      else next(error);
    }
  });
  const recheck = async (): Promise<void> => {
    const route = router.currentRoute;
    if (!['secret_list', 'secret_detail', 'secret_create'].includes(route.name)) return;
    // Capture the entry before awaiting: another navigation/account change may update start.filter.
    const filter = route.name === 'secret_list' ? stringifyPostsRoute(route, store.state.common.start.start.filter) : '';
    activeChecks += 1;
    privateContentVisibility.checking = true;
    try {
      const redirect = await check(route);
      if (router.currentRoute.fullPath !== route.fullPath) return;
      if (!redirect && route.name === 'secret_list') {
        store.commit('secret/list/discardContent', { filter });
        await store.dispatch(`secret/list/${SECRET.GET_POST_LIST}`, {
          filter, kw: route.query.kw, sort: route.query.sort, action: 'reload', route,
        });
      }
      if (redirect && router.currentRoute.fullPath === route.fullPath) await router.replace(redirect).catch(() => {});
    } catch (error) {
      if (protectedRoutes[route.fullPath] && router.currentRoute.fullPath === route.fullPath) {
        await router.replace({ name: 'private_rooms' }).catch(() => {});
      } else if (router.currentRoute.fullPath === route.fullPath) {
        showToast({ text: '内容刷新失败，请下拉重试', time: 2500 });
      }
    } finally {
      activeChecks -= 1;
      privateContentVisibility.checking = document.hidden || activeChecks > 0;
    }
  };
  store.watch(state => state.user.user && state.user.user.id, (id, previousId) => {
    store.dispatch('secret/privateRooms/invalidate');
    if (previousId && previousId !== id && ['secret_list', 'secret_detail', 'secret_create'].includes(router.currentRoute.name)) router.replace({ name: 'private_rooms' }).catch(() => {});
  });
  store.watch(state => state.secret.privateRooms.revision, recheck);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) privateContentVisibility.checking = true;
    else {
      privateContentVisibility.checking = activeChecks > 0;
      recheck();
    }
  });
  window.addEventListener('pageshow', recheck);
};
